import {Grid} from '@material-ui/core';
import {CompensationProcessingType} from 'Quoting/api/types';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {getPredefinedHierarchicalContracts, refreshProducerLevels} from 'Quoting/ducks/compensation';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {isDefined} from 'Quoting/utils/utilities';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {CONRACTED_COMPENSATION_FIELD_ID} from '../Plan/PlanTemplates/GWL/gpluConstants';
import {getContractedCommissionsBlockPropertyPaths} from './propertyPaths';

export const ContractedCommissionsBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, updateQuoteFieldValues, quote} = useQuoteField();
  const {processingType} = useSelector((state: ReducerState) => state.compensation);

  const dispatch = useDispatch();

  const propertyPaths = getContractedCommissionsBlockPropertyPaths();

  const productModelName = getQuoteFieldValue('_modelName') as A360ProductModel;

  const quotedEnrollmentType = getQuoteFieldValue(propertyPaths.quotedEnrollmentType) as string;

  const quotedCommissionSeries = getQuoteFieldValue(propertyPaths.quotedCommissionSeries) as string;

  const quotedCommissionLevel = getQuoteFieldValue(propertyPaths.quotedCommissionLevel) as string;

  const isFlatPercentProcessingType = processingType === CompensationProcessingType.FlatPercent;

  const isIndividualHeapedPercentProcessingType =
    !isFlatPercentProcessingType && processingType === CompensationProcessingType.IndividualHeapedPercent;

  useEffect(() => {
    // call predefined-hierarchical-contracts when either of quotedEnrollmentType, quotedCommissionSeries, quotedCommissionLevel changes
    if (isDefined(quotedEnrollmentType) && isDefined(quotedCommissionSeries) && isDefined(quotedCommissionLevel)) {
      dispatch(getPredefinedHierarchicalContracts(quote?.Data));
    }
  }, [quotedEnrollmentType, quotedCommissionSeries, quotedCommissionLevel]);

  useEffect(() => {
    // call retrieve attribute values endpoint when series changes from E,K,X to Y (track changes to processing type instead of series)
    dispatch(refreshProducerLevels(productModelName, quotedCommissionSeries));
  }, [quotedCommissionSeries]);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'row-5b1931cf-7478-4784-be2a-240b1dfcf578'},
      items: [
        {
          key: 'd5d79eb2-0973-4b30-81c4-af82c736b91c',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Commission Style"
              id={propertyPaths.commissionStyle}
              value={getQuoteFieldValue(propertyPaths.commissionStyle)}
              onChange={e => updateQuoteFieldValue(propertyPaths.commissionStyle, e.target.value)}
              searchKey={`${productModelName}PolicySummary.commissionStyle`}
              formFieldId={CONRACTED_COMPENSATION_FIELD_ID.commissionStyle}
              required
              disabled
            />
          ),
        },
        {
          key: 'b22daf74-42da-4868-a35b-ad6d0b8f486d',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Quoted Enrollment Type"
              id={propertyPaths.quotedEnrollmentType}
              value={quotedEnrollmentType}
              onChange={e => updateQuoteFieldValue(propertyPaths.quotedEnrollmentType, e.target.value)}
              searchKey={`${productModelName}PolicySummary.quotedEnrollmentType`}
              formFieldId={CONRACTED_COMPENSATION_FIELD_ID.quotedEnrollmentType}
              required
            />
          ),
        },
        {
          key: '101b6f02-4c9f-4e5c-9592-15d4810f078d',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Quoted Commission Series"
              id={propertyPaths.quotedCommissionSeries}
              value={quotedCommissionSeries}
              onChange={e =>
                updateQuoteFieldValues([
                  {path: propertyPaths.quotedCommissionSeries, newValue: e.target.value},
                  {path: propertyPaths.quotedCommissionLevel, newValue: undefined},
                  {path: propertyPaths.quotedCommissionRate, newValue: undefined},
                  {path: propertyPaths.quotedCommissionFYRate, newValue: undefined},
                  {path: propertyPaths.quotedCommissionRenewalRate, newValue: undefined},
                ])
              }
              searchKey={`${productModelName}PolicySummary.quotedCommissionSeries`}
              formFieldId={CONRACTED_COMPENSATION_FIELD_ID.quotedCommissionSeries}
              required
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'row-f6614ff7-5ad1-4240-985f-590926dde112'},
      items: [
        {
          key: '75fd22af-a3cc-4ad5-a977-530b4659113a',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              label="Quoted Commission Level"
              id={propertyPaths.quotedCommissionLevel}
              value={quotedCommissionLevel}
              onChange={e => updateQuoteFieldValue(propertyPaths.quotedCommissionLevel, e.target.value)}
              searchKey={`${productModelName}PolicySummary.quotedCommissionLevel`}
              formFieldId={CONRACTED_COMPENSATION_FIELD_ID.quotedCommissionLevel}
              required
            />
          ),
        },
        {
          key: '511916db-8fd5-4244-a847-ec856661122e',
          hidden:
            isIndividualHeapedPercentProcessingType ||
            !isDefined(quotedCommissionLevel) ||
            !isDefined(getQuoteFieldValue(propertyPaths.quotedCommissionRate)),
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Quoted Commission Rate"
              id={propertyPaths.quotedCommissionRate}
              value={getQuoteFieldValue(propertyPaths.quotedCommissionRate)}
              onChange={e => updateQuoteFieldValue(propertyPaths.quotedCommissionRate, Number(e.target.value))}
              formFieldId={CONRACTED_COMPENSATION_FIELD_ID.quotedCommissionRate}
              type="number"
              disabled
              required
            />
          ),
        },
        {
          key: '2a7d035a-a561-4465-9d74-70ae778166e8',
          hidden:
            isFlatPercentProcessingType ||
            !isDefined(quotedCommissionLevel) ||
            !isDefined(getQuoteFieldValue(propertyPaths.quotedCommissionFYRate)),
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Quoted Commission First Year Rate"
              id={propertyPaths.quotedCommissionFYRate}
              value={getQuoteFieldValue(propertyPaths.quotedCommissionFYRate)}
              onChange={e => updateQuoteFieldValue(propertyPaths.quotedCommissionFYRate, Number(e.target.value))}
              formFieldId={CONRACTED_COMPENSATION_FIELD_ID.quotedCommissionFYRate}
              type="number"
              disabled
              required
            />
          ),
        },
        {
          key: 'b4ff5346-4600-4613-852f-5edd2bb88438',
          hidden:
            isFlatPercentProcessingType ||
            !isDefined(quotedCommissionLevel) ||
            !isDefined(getQuoteFieldValue(propertyPaths.quotedCommissionRenewalRate)),
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Quoted Commission Renewal Rate"
              id={propertyPaths.quotedCommissionRenewalRate}
              value={getQuoteFieldValue(propertyPaths.quotedCommissionRenewalRate)}
              onChange={e => updateQuoteFieldValue(propertyPaths.quotedCommissionRenewalRate, Number(e.target.value))}
              formFieldId={CONRACTED_COMPENSATION_FIELD_ID.quotedCommissionRenewalRate}
              type="number"
              disabled
              required
            />
          ),
        },
      ],
    },
  ];

  return (
    <Grid container alignItems="center">
      <FormRowGrid rows={rows} />
    </Grid>
  );
};
